import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  CollapseAllButton,
  DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
  Divider,
  EMPTY_ARRAY,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  SlippageControl,
  Toggle,
  getTreeRowBlotterGroupColDef,
  useAccordionFilterBuilder,
  useBlotterTable,
  useCallbackRef,
  usePersistedBlotterTable,
  usePopoverState,
  type Quote,
  type RelatedEntitiesConfig,
  type Slippage,
} from '@talos/kyoko';

import { useDetailsDrawer } from '@src/components/DetailsDrawer/useDetailsDrawer';
import { rfqSliceActions } from '@src/components/OMS/NewRFQ/RFQSlice';
import { openView } from '@src/components/OMS/OMSSlice';
import { OMSView } from '@src/components/OMS/OMSView';
import { RfqBestQuoteByToggle } from '@src/components/OMS/RfqBestQuoteByToggle';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import { useAppStateDispatch } from '@src/providers/AppStateProvider/AppStateProviderHooks';
import { BLOTTER_TRADE_BEST_SLIPPAGE } from '@src/tokens/tooltips';
import type { GridOptions } from 'ag-grid-community';
import { useMemo, useRef } from 'react';
import { QuotesV2GroupByButtons } from './QuotesV2GroupByButtons';
import { QuoteMarketRow, QuoteRow, type QuotesBlotterRow } from './types';
import { useQuotesV2BlotterDataObs } from './useQuotesV2BlotterDataObs';
import { useQuotesV2Columns } from './useQuotesV2Columns';
import type { QuotesV2TableFilter } from './useQuotesV2Filter';
import { useQuotesV2Filter } from './useQuotesV2Filter';
import { useQuotesV2GroupBy, type QuotesV2GroupBy } from './useQuotesV2GroupBy';
import { useQuotesV2Menu } from './useQuotesV2Menu';

export interface QuotesV2BlotterSettings {
  showAllDecimals?: boolean;
  groupBy?: QuotesV2GroupBy;
}

export interface QuotesV2BlotterTableProps {
  blotterID: string;
  defaultFilter?: QuotesV2TableFilter;
  showStatusFilter?: boolean;
  initialIsOpen?: boolean;
  settings?: QuotesV2BlotterSettings;
  onSettingsChanged?: (settings: QuotesV2BlotterSettings) => void;
}

export function QuotesV2BlotterTable({
  blotterID,
  defaultFilter,
  initialIsOpen,
  settings,
  showStatusFilter,
  onSettingsChanged,
}: QuotesV2BlotterTableProps) {
  const { showAllDecimals, groupBy: inputGroupBy = 'RFQ' } = settings ?? {};
  const { showAllDecimalsInBlotters, allowedSlippage, setAllowedSlippage, rfqBestQuoteBy } = useTradingSettings();
  const dispatch = useAppStateDispatch();

  const { availableOptions, selectedGroupBy, groupHeaderPrefix, rowGroupingOptions } = useQuotesV2GroupBy({
    groupBy: inputGroupBy,
  });
  const showGroupByTabs = availableOptions.length > 1 && onSettingsChanged != null;

  const onOpenQuote = useCallbackRef((quote: Quote) => {
    dispatch(openView(OMSView.RFQForm));
    dispatch(rfqSliceActions.showRFQ(quote));
  });

  const { openDetailsDrawer } = useDetailsDrawer();
  const onShowDetails = useCallbackRef((quote: Quote) => {
    openDetailsDrawer({ entity: quote });
  });

  const handleRowDoubleClicked: NonNullable<GridOptions<QuotesBlotterRow>['onRowDoubleClicked']> = useCallbackRef(
    ({ data }) => {
      if (data instanceof QuoteRow) {
        onShowDetails(data.quote);
      }
    }
  );

  const columns = useQuotesV2Columns({ onOpenQuote, onShowDetails });
  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });
  const relatedEntities = useMemo<RelatedEntitiesConfig<QuotesBlotterRow>>(
    () => ({
      symbolSources: [
        {
          id: 'Symbol',
          title: 'Security',
          lookup: 'security',
          optional: true,
          getSymbol: row => {
            if (row instanceof QuoteRow) {
              return row.quote.Symbol;
            }

            if (row instanceof QuoteMarketRow) {
              return row.parentQuote.Symbol;
            }

            return undefined;
          },
        },
      ],
    }),
    []
  );

  const { filterFn, filter, blotterTableFilterProps, filterBuilderProps } = useQuotesV2Filter({
    initialFilter: persistedBlotterTable.initialState?.filter,
    onFilterChanged: persistedBlotterTable.onFilterChanged,
    showStatusFilter,
  });

  const filterBuilderAccordion = useAccordionFilterBuilder({
    accordionProps: { initialOpen: initialIsOpen },
    filterBuilderProps,
  });

  const options = useMemo(
    () => ({
      ...rowGroupingOptions,
      bestBy: rfqBestQuoteBy,
    }),
    [rfqBestQuoteBy, rowGroupingOptions]
  );

  const { dataObservable, errorObservable, quotesObs } = useQuotesV2BlotterDataObs({
    filterFn,
    filter,
    options,
  });

  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals: (showAllDecimalsInBlotters || showAllDecimals) ?? false,
    }),
    [showAllDecimalsInBlotters, showAllDecimals]
  );

  const { getCustomContextMenuItems, dialogs } = useQuotesV2Menu({
    filterableProperties: filterBuilderProps.properties,
    openClause: filterBuilderAccordion.openClause,
    quoteSub: quotesObs,
  });

  const gridOptions: GridOptions<QuotesBlotterRow> = useMemo(() => {
    return {
      rowSelection: DEFAULT_BLOTTER_SELECTION_MULTI_PARAMS,
      groupDisplayType: 'singleColumn',
      treeData: true,
      getDataPath: row => row.dataPath,
      processUnpinnedColumns: () => EMPTY_ARRAY,
      onRowDoubleClicked: handleRowDoubleClicked,
      autoGroupColumnDef: {
        headerName: groupHeaderPrefix ? `${groupHeaderPrefix} > RFQ > Market` : 'RFQ > Market',
        width: 200,
        editable: false,
        suppressColumnsToolPanel: false,
        cellRendererParams: {
          suppressCount: true,
        },
        sortable: false,
        pinned: 'left',
        ...getTreeRowBlotterGroupColDef(),
      },
    };
  }, [groupHeaderPrefix, handleRowDoubleClicked]);

  const blotterTable = useBlotterTable<QuotesBlotterRow>({
    dataObservable,
    errorObservable,
    columns,
    persistence: persistedBlotterTable,
    rowID: 'rowID' satisfies keyof QuotesBlotterRow,
    gridOptions,
    contextMenuOptions: { getCustomContextMenuItems, relatedEntities },
    settings: blotterTableSettings,
    exportOptions: {
      name: 'RFQs',
      baseParams: { skipRowGroups: false },
    },
  });

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const slippagePopoverContentRef = useRef<HTMLDivElement | null>(null);
  const extrasMenuCloseRef = useRef<() => void>(() => {});

  const isInsideSlippagePopover = (target: EventTarget | null) => {
    const node = target as Node | null;
    if (node && slippagePopoverContentRef.current?.contains(node)) {
      return true;
    }
    return false;
  };

  const extrasMenuPopover = usePopoverState({
    closeOnClickOutside: false,
    trigger: 'click',
    placement: 'bottom-end',
    usePortal: true,
    onClickOutside: e => {
      if (isInsideSlippagePopover(e.target)) {
        return;
      }

      extrasMenuCloseRef.current();
    },
  });
  extrasMenuCloseRef.current = extrasMenuPopover.close;

  const handleExpandAllGroups = useCallbackRef(() => {
    expandAllGroups();
  });

  const handleCollapseAllGroups = useCallbackRef(() => {
    collapseAllGroups();
  });

  const handleApplySlippage = useCallbackRef((value: Slippage) => {
    setAllowedSlippage(value);
  });

  const handleGroupByChange = useCallbackRef((groupBy: QuotesV2GroupBy) => {
    onSettingsChanged?.({
      ...settings,
      groupBy,
    });
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTableFilterProps}
        {...blotterTable.blotterTableFiltersProps}
        quickFilterPrefix={
          showGroupByTabs && (
            <QuotesV2GroupByButtons
              selectedGroupBy={selectedGroupBy}
              options={availableOptions}
              onChange={handleGroupByChange}
            />
          )
        }
        prefix={
          <>
            <ExpandAllButton onClick={handleExpandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={handleCollapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            {!showAllDecimalsInBlotters && onSettingsChanged != null && (
              <Toggle
                label="Show All Decimals"
                size={FormControlSizes.Small}
                checked={showAllDecimals ?? false}
                onChange={value => onSettingsChanged({ ...settings, showAllDecimals: value })}
              />
            )}

            <RfqBestQuoteByToggle size={FormControlSizes.Small} />

            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />

            <Divider />

            <SlippageControl
              slippage={allowedSlippage}
              onApply={handleApplySlippage}
              popoverContentRef={slippagePopoverContentRef}
              helpTooltip={BLOTTER_TRADE_BEST_SLIPPAGE(rfqBestQuoteBy)}
              placement="bottom"
            />
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      {dialogs}
    </>
  );
}
